/**
 * Freeze windows and landing rules as policy (architecture doc 7.1): a window stops landing,
 * deploys or new sessions for the org or one project, once (from..to) or every week (days
 * and a time range, which may run past midnight). Pure; the caller passes the clock.
 */
import type { ActiveFreeze, FreezeBlock, FreezeWindow, Id, LandingDecision, LandingReviewer, PolicyConfig, Ts } from '@orgus/protocol';
import { resolveSettings } from './settings.ts';

const DAY = 86_400_000;
const MIN = 60_000;

/** 'HH:MM' to minutes after midnight, or null. */
export function parseClock(s: string | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((s ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
}

/** When this window ends if it is in force at `at`, else null. */
export function freezeUntil(w: FreezeWindow, at: Ts): Ts | null {
  if (w.enabled === false) return null;
  if (w.kind === 'once') {
    if (w.from == null || w.to == null) return null;
    return at >= w.from && at < w.to ? w.to : null;
  }
  const start = parseClock(w.start);
  const end = parseClock(w.end);
  const days = w.days ?? [];
  if (start == null || end == null || !days.length) return null;
  const off = (w.utcOffsetMinutes ?? 0) * MIN;
  const local = at + off;
  const dayStart = Math.floor(local / DAY) * DAY;
  const day = new Date(dayStart).getUTCDay();
  const minute = (local - dayStart) / MIN;
  if (end > start) {
    if (days.includes(day) && minute >= start && minute < end) return dayStart + end * MIN - off;
    return null;
  }
  // Runs past midnight: the evening part of a listed day, or the morning after one.
  if (days.includes(day) && minute >= start) return dayStart + DAY + end * MIN - off;
  if (days.includes((day + 6) % 7) && minute < end) return dayStart + end * MIN - off;
  return null;
}

/** Windows in force now for this project (org windows apply everywhere), optionally only those blocking one thing. */
export function activeFreezes(windows: readonly FreezeWindow[], projectId: Id | null, at: Ts, block?: FreezeBlock): ActiveFreeze[] {
  const out: ActiveFreeze[] = [];
  for (const w of windows) {
    if (w.scope.kind === 'project' && w.scope.projectId !== projectId) continue;
    if (block && !w.blocks.includes(block)) continue;
    const until = freezeUntil(w, at);
    if (until != null) out.push({ window: w, until });
  }
  return out.sort((a, b) => b.until - a.until);
}

/** May an item land in this project now? */
export function landingDecision(config: PolicyConfig, projectId: Id | null, at: Ts): LandingDecision {
  const settings = resolveSettings(config, projectId);
  const requiredChecks = (settings.find((s) => s.key === 'requiredChecks')?.value as string[] | undefined) ?? [];
  const reviewer = (settings.find((s) => s.key === 'reviewer')?.value as LandingReviewer | undefined) ?? 'none';
  const frozen = activeFreezes(config.freezes ?? [], projectId, at, 'landing')[0] ?? null;
  const reason = frozen
    ? `Landing is frozen by "${frozen.window.name}"${frozen.window.reason ? ` (${frozen.window.reason})` : ''}.`
    : requiredChecks.length
      ? `Landing needs ${requiredChecks.join(', ')} to pass${reviewer !== 'none' ? ` and a ${reviewer.replace(/-/g, ' ')} review` : ''}.`
      : 'No landing rules apply.';
  return { allowed: !frozen, frozen, requiredChecks, reviewer, reason };
}
